// ./src/components/features/Pois/PoisList/PoisList.tsx
//
// Event Points of Interest list — table on desktop (>= md), cards on tablet/mobile.
// Tap-safe (explicit Edit/Delete only), rights-gated per row, selection-ready.
// Category name + icon/color are resolved from the shared CategoriesContext. Mirrors
// the Highlights list conventions (see em-scaffold-ui).

import React, { useCallback, useMemo } from "react";
import {
  Paper,
  Stack,
  Group,
  Text,
  Box,
  Badge,
  ThemeIcon,
  ActionIcon,
  Tooltip,
  Skeleton,
  Table,
  Checkbox,
} from "@mantine/core";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { STATUSES } from "@sdflc/utils";

import { PointOfInterest } from "@/utils/api";
import { UpvoteCountBadge } from "@/components/features/Upvotes/UpvoteCountBadge";
import { RowSelection } from "@/hooks";
import { useAppEnvironment } from "@/contexts";
import { useOptionalPointsOfInterestContext } from "@/contexts/PointsOfInterestContext";
import { useOptionalCategoriesContext } from "@/contexts/CategoriesContext";
import {
  AppIcon,
  EmptyState,
  SwipeableCard,
  SwipeAction,
  useSwipeableList,
  LoadMoreButton,
  CardSkeletonActions,
  CardSkeletonLines,
  CardSkeletonCheckbox,
  countCardActions,
  type CardSkeletonShape,
} from "@/components/generic";
import {
  useEntranceAnimation,
  entranceListVariants,
  entranceCardVariants,
} from "@/hooks/useEntranceAnimation";
import { useEntityCommentsDrawer } from "@/components/features/Comments";
import { recordHasRight, HIGHLIGHT_STYLE, HIGHLIGHT_ROW_BG, formatCurrency } from "@/utils";

// ============================================
// Props
// ============================================

/** A category section for the grouped list (heading + its items, already ordered). */
export interface PoiGroup {
  key: string;
  label: string;
  icon?: string | null;
  color?: string | null;
  items: PointOfInterest[];
}

export interface PoisListProps {
  data?: PointOfInterest[];
  /**
   * Render items grouped under category headings (order = as given). When set, it
   * drives the rows; `data`/context items are ignored for layout (the flat list is
   * derived from the groups for selection + empty-state logic).
   */
  groups?: PoiGroup[];
  loading?: boolean;
  loadingMore?: boolean;
  hasMore?: boolean;
  onLoadMore?: () => void;
  onEditPoi?: (poi: PointOfInterest) => void;
  onDeletePoi?: (poi: PointOfInterest) => void;
  onManageGuests?: (poi: PointOfInterest) => void;
  isRemoving?: (id?: string) => boolean;
  highlightedId?: string | null;
  onAddPoi?: () => void;
  selection?: RowSelection;
  selectable?: boolean;
  /** True when a search term or filter is narrowing the list — shows a "no match" message instead of the empty state. */
  hasQueryNarrowing?: boolean;
}

// ============================================
// Shared bits
// ============================================

const isDisabled = (poi: PointOfInterest): boolean => poi.status === STATUSES.DISABLED;

/** A row is editable when the caller has any right on it (BE sets [] for read-only rows). */
const isEditableRow = (poi: PointOfInterest): boolean =>
  recordHasRight(poi, "edit") || recordHasRight(poi, "remove");

/** `price (in its currency) / priceUnit`, dropping any part that isn't set. */
const formatPrice = (poi: PointOfInterest): string | null => {
  if (poi.price === null || poi.price === undefined) {
    return null;
  }

  // #A4.7b — formatted in the EVENT's currency (every row carries it), e.g. "CA$120.00".
  const amount = poi.currencyCode ? formatCurrency(Number(poi.price), poi.currencyCode) : `${poi.price}`;

  return poi.priceUnit ? `${amount} / ${poi.priceUnit}` : amount;
};

interface CategoryMeta {
  name: string | null;
  icon: string | null;
  color: string | null;
}

const StatusBadge: React.FC<{ poi: PointOfInterest }> = ({ poi }) => {
  const { t } = useTranslation(["common"]);

  if (!isDisabled(poi)) {
    return null;
  }

  return (
    <Badge size="xs" variant="light" color="gray">
      {t("common:status.disabled", { defaultValue: "Disabled" })}
    </Badge>
  );
};

const CategoryCell: React.FC<{ meta: CategoryMeta | null }> = ({ meta }) => {
  if (!meta?.name) {
    return (
      <Text size="sm" c="dimmed">
        —
      </Text>
    );
  }

  return (
    <Group gap={6} wrap="nowrap">
      {meta.icon ? <AppIcon iconName={meta.icon} size={16} color={meta.color || undefined} /> : null}
      <Text size="md" c={meta.color || undefined} lineClamp={1}>
        {meta.name}
      </Text>
    </Group>
  );
};

const PoiIcon: React.FC<{ meta: CategoryMeta | null; size?: number }> = ({ meta, size = 34 }) => (
  <ThemeIcon variant="light" color={meta?.color || "blue"} radius="xl" size={size}>
    <AppIcon iconName={meta?.icon || "MapPin"} size={Math.round(size * 0.55)} />
  </ThemeIcon>
);

const RowActions: React.FC<{
  poi: PointOfInterest;
  onEdit?: (p: PointOfInterest) => void;
  onDelete?: (p: PointOfInterest) => void;
  onManageGuests?: (p: PointOfInterest) => void;
  onOpenComments?: (p: PointOfInterest) => void;
  isDeleting: boolean;
}> = ({ poi, onEdit, onDelete, onManageGuests, onOpenComments, isDeleting }) => {
  const { t } = useTranslation(["events", "common"]);

  const showEdit = !!onEdit && recordHasRight(poi, "edit");
  const showDelete = !!onDelete && recordHasRight(poi, "remove");
  const showGuests = !!onManageGuests && recordHasRight(poi, "edit");
  const showComments = !!onOpenComments; // comments are open to any viewer of the item

  if (!showEdit && !showDelete && !showGuests && !showComments) {
    return null;
  }

  return (
    <Group gap={4} wrap="nowrap">
      {showComments && onOpenComments && (
        <Tooltip label={t("common:comments.action", { defaultValue: "Comments" })} withArrow position="bottom">
          <ActionIcon
            variant="subtle"
            color="gray"
            size="md"
            onClick={() => onOpenComments(poi)}
            disabled={isDeleting}
            aria-label={t("common:comments.action", { defaultValue: "Comments" })}
          >
            <AppIcon iconName="Message" size={16} />
          </ActionIcon>
        </Tooltip>
      )}
      {showGuests && onManageGuests && (
        <Tooltip label={t("events:pois.guests.action", { defaultValue: "Guests signed up" })} withArrow position="bottom">
          <ActionIcon
            variant="subtle"
            color="gray"
            size="md"
            onClick={() => onManageGuests(poi)}
            disabled={isDeleting}
            aria-label={t("events:pois.guests.action", { defaultValue: "Guests signed up" })}
          >
            <AppIcon iconName="Users" size={16} />
          </ActionIcon>
        </Tooltip>
      )}
      {showEdit && onEdit && (
        <Tooltip label={t("common:buttons.edit")} withArrow position="bottom">
          <ActionIcon
            variant="subtle"
            color="blue"
            size="md"
            onClick={() => onEdit(poi)}
            disabled={isDeleting}
            aria-label={t("common:buttons.edit")}
          >
            <AppIcon iconName="Edit" size={16} />
          </ActionIcon>
        </Tooltip>
      )}
      {showDelete && onDelete && (
        <Tooltip label={t("common:buttons.delete")} withArrow position="bottom">
          <ActionIcon
            variant="subtle"
            color="red"
            size="md"
            onClick={() => onDelete(poi)}
            loading={isDeleting}
            disabled={isDeleting}
            aria-label={t("common:buttons.delete")}
          >
            <AppIcon iconName="Trash" size={16} />
          </ActionIcon>
        </Tooltip>
      )}
    </Group>
  );
};

// ============================================
// Row shape
// ============================================

interface RowProps {
  poi: PointOfInterest;
  name: string;
  meta: CategoryMeta | null;
  price: string | null;
  editable: boolean;
  disabled: boolean;
  onEdit?: (p: PointOfInterest) => void;
  onDelete?: (p: PointOfInterest) => void;
  onManageGuests?: (p: PointOfInterest) => void;
  onOpenComments?: (p: PointOfInterest) => void;
  highlighted: boolean;
  isDeleting: boolean;
  selectionColumn: boolean;
  selectable: boolean;
  selected: boolean;
  selectionMode: boolean;
  onToggleSelect?: (id: string) => void;
}

// ============================================
// Desktop table row
// ============================================

const PoiTableRow: React.FC<RowProps> = ({
  poi,
  name,
  meta,
  price,
  disabled,
  onEdit,
  onDelete,
  onManageGuests,
  onOpenComments,
  highlighted,
  isDeleting,
  selectionColumn,
  selectable,
  selected,
  onToggleSelect,
}) => {
  return (
    <Table.Tr
      style={{
        opacity: isDeleting ? 0.5 : disabled ? 0.6 : 1,
        backgroundColor: highlighted
          ? HIGHLIGHT_ROW_BG
          : selected
          ? "var(--mantine-color-blue-light)"
          : undefined,
        transition: "background-color 150ms ease, opacity 150ms ease",
      }}
    >
      {selectionColumn && (
        <Table.Td style={{ width: 44 }}>
          {selectable && (
            <Checkbox
              checked={selected}
              onChange={() => poi.id && onToggleSelect?.(poi.id)}
              disabled={isDeleting}
              aria-label={name}
            />
          )}
        </Table.Td>
      )}
      <Table.Td>
        <Group gap="sm" wrap="nowrap">
          <PoiIcon meta={meta} />
          <Box style={{ minWidth: 0 }}>
            <Group gap={6} wrap="nowrap">
              <Text size="md" fw={600} lineClamp={1}>
                {name}
              </Text>
              <StatusBadge poi={poi} />
              <UpvoteCountBadge count={poi.upvoteCount} />
            </Group>
          </Box>
        </Group>
      </Table.Td>
      <Table.Td style={{ width: 200 }}>
        <CategoryCell meta={meta} />
      </Table.Td>
      <Table.Td style={{ width: 240 }}>
        <Text size="sm" c="dimmed" lineClamp={1}>
          {poi.streetAddress || "—"}
        </Text>
      </Table.Td>
      <Table.Td style={{ width: 180 }}>
        <Text size="md" lineClamp={1}>
          {price || "—"}
        </Text>
      </Table.Td>
      <Table.Td style={{ width: 100 }}>
        <RowActions poi={poi} onEdit={onEdit} onDelete={onDelete} onManageGuests={onManageGuests} onOpenComments={onOpenComments} isDeleting={isDeleting} />
      </Table.Td>
    </Table.Tr>
  );
};

const PoiTableRowSkeleton: React.FC<{ selectionColumn?: boolean }> = ({ selectionColumn }) => (
  <Table.Tr>
    {selectionColumn && (
      <Table.Td style={{ width: 44 }}>
        <Skeleton height={20} width={20} radius="sm" />
      </Table.Td>
    )}
    <Table.Td>
      <Group gap="sm" wrap="nowrap">
        <Skeleton circle height={34} width={34} />
        <Skeleton height={14} width="40%" maw={200} />
      </Group>
    </Table.Td>
    <Table.Td style={{ width: 200 }}>
      <Skeleton height={14} width={120} />
    </Table.Td>
    <Table.Td style={{ width: 240 }}>
      <Skeleton height={14} width={160} />
    </Table.Td>
    <Table.Td style={{ width: 180 }}>
      <Skeleton height={14} width={100} />
    </Table.Td>
    <Table.Td style={{ width: 100 }}>
      <Group gap={4} wrap="nowrap">
        <Skeleton circle height={28} width={28} />
        <Skeleton circle height={28} width={28} />
      </Group>
    </Table.Td>
  </Table.Tr>
);

// ============================================
// Card (tablet / mobile)
// ============================================

interface CardProps extends RowProps {
  forceClose?: boolean;
  onForceCloseComplete?: () => void;
  onSwipeStateChange?: (isOpen: boolean) => void;
}

const PoiCardComponent: React.FC<CardProps> = ({
  poi,
  name,
  meta,
  price,
  disabled,
  onEdit,
  onDelete,
  onManageGuests,
  onOpenComments,
  highlighted,
  isDeleting,
  selectable,
  selected,
  selectionMode,
  onToggleSelect,
  forceClose,
  onForceCloseComplete,
  onSwipeStateChange,
}) => {
  const { t } = useTranslation(["events", "common"]);

  const toggleSelect = () => poi.id && onToggleSelect?.(poi.id);

  const swipeActions = useMemo((): SwipeAction[] => {
    const actions: SwipeAction[] = [];

    // Secondary actions (comments + manage-guests) auto-route to the LEFT swipe side; edit/remove stay right.
    if (onOpenComments) {
      actions.push({
        key: "comments",
        icon: "Message",
        label: t("common:comments.action", { defaultValue: "Comments" }),
        color: "gray",
        onClick: () => onOpenComments(poi),
        disabled: isDeleting,
        secondary: true,
      });
    }

    if (onManageGuests && recordHasRight(poi, "edit")) {
      actions.push({
        key: "guests",
        icon: "Users",
        label: t("events:pois.guests.action", { defaultValue: "Guests signed up" }),
        color: "gray",
        onClick: () => onManageGuests(poi),
        disabled: isDeleting,
        secondary: true,
      });
    }

    if (onEdit && recordHasRight(poi, "edit")) {
      actions.push({
        key: "edit",
        icon: "Edit",
        label: t("common:buttons.edit"),
        color: "blue",
        onClick: () => onEdit(poi),
        disabled: isDeleting,
      });
    }

    if (onDelete && recordHasRight(poi, "remove")) {
      actions.push({
        key: "delete",
        icon: "Trash",
        label: t("common:buttons.delete"),
        color: "red",
        onClick: () => onDelete(poi),
        loading: isDeleting,
        disabled: isDeleting,
      });
    }

    return actions;
  }, [onEdit, onDelete, onManageGuests, onOpenComments, poi, isDeleting, t]);

  const cardBody = (showActions: boolean, showCheckbox: boolean) => (
    <Paper
      p="md"
      withBorder
      radius="md"
      opacity={isDeleting ? 0.5 : disabled ? 0.6 : 1}
      style={{
        transition: "opacity 150ms ease, box-shadow 150ms ease",
        backgroundColor: selected ? "var(--mantine-color-blue-light)" : undefined,
        ...(highlighted ? HIGHLIGHT_STYLE : {}),
      }}
    >
      <Group gap="md" wrap="nowrap" align="flex-start">
        {showCheckbox && (
          <Checkbox
            mt={2}
            checked={selected}
            onChange={toggleSelect}
            onClick={(e) => e.stopPropagation()}
            disabled={isDeleting}
            aria-label={name}
          />
        )}
        <PoiIcon meta={meta} size={40} />
        <Box style={{ flex: 1, minWidth: 0 }}>
          <Group gap={6} wrap="nowrap">
            <Text size="md" fw={600} lineClamp={1}>
              {name}
            </Text>
            <StatusBadge poi={poi} />
            <UpvoteCountBadge count={poi.upvoteCount} />
          </Group>
          <Box mt={4}>
            <CategoryCell meta={meta} />
          </Box>
          {poi.streetAddress && (
            <Text size="sm" c="dimmed" lineClamp={1} mt={2}>
              {poi.streetAddress}
            </Text>
          )}
          {price && (
            <Text size="md" mt={2}>
              {price}
            </Text>
          )}
        </Box>
        {showActions && <RowActions poi={poi} onEdit={onEdit} onDelete={onDelete} onManageGuests={onManageGuests} onOpenComments={onOpenComments} isDeleting={isDeleting} />}
      </Group>
    </Paper>
  );

  if (selectionMode) {
    return (
      <>
        <Box
          hiddenFrom="sm"
          onClick={selectable ? toggleSelect : undefined}
          style={{ cursor: selectable ? "pointer" : "default", userSelect: "none" }}
        >
          {cardBody(false, selectable)}
        </Box>
        <Box visibleFrom="sm">{cardBody(true, selectable)}</Box>
      </>
    );
  }

  return (
    <>
      <Box hiddenFrom="sm">
        {swipeActions.length > 0 ? (
          <SwipeableCard
            leftSwipeActions={swipeActions}
            forceClose={forceClose}
            onForceCloseComplete={onForceCloseComplete}
            onSwipeStateChange={onSwipeStateChange}
            disabled={isDeleting}
            borderRadius="var(--mantine-radius-md)"
          >
            {cardBody(false, false)}
          </SwipeableCard>
        ) : (
          cardBody(false, false)
        )}
      </Box>
      <Box visibleFrom="sm">{cardBody(true, selectable)}</Box>
    </>
  );
};

const PoiCard = React.memo(PoiCardComponent);

const PoiCardSkeleton: React.FC<CardSkeletonShape> = ({ actions, withCheckbox }) => (
  <Paper p="md" withBorder radius="md">
    <Group gap="md" wrap="nowrap" align="flex-start">
      <CardSkeletonCheckbox show={withCheckbox} />
      <Skeleton circle height={40} width={40} />
      <CardSkeletonLines count={3} sizes={['md', 'md', 'sm']} gap={3} />
      <CardSkeletonActions count={actions} />
    </Group>
  </Paper>
);

// ============================================
// Main
// ============================================

const PoisListComponent: React.FC<PoisListProps> = ({
  data: dataProp,
  groups,
  loading: loadingProp,
  loadingMore: loadingMoreProp,
  hasMore: hasMoreProp,
  onLoadMore: onLoadMoreProp,
  onEditPoi,
  onDeletePoi,
  onManageGuests,
  isRemoving: isRemovingProp,
  highlightedId,
  onAddPoi,
  selection,
  selectable = false,
  hasQueryNarrowing = false,
}) => {
  const { t } = useTranslation(["events", "common"]);
  const { isDesktop } = useAppEnvironment();

  const isSelectable = selectable && !!selection;
  // The tablet card's row buttons, one per handler — what the card skeleton draws (▶ check-card-skeletons).
  const cardActions = countCardActions(onEditPoi, onDeletePoi, onManageGuests, true /* comments — open to every viewer */);

  const poisContext = useOptionalPointsOfInterestContext();
  const categoriesContext = useOptionalCategoriesContext();

  // When grouped, the flat list (for selection, skeleton, empty-state) is derived
  // from the groups so callers pass only `groups`.
  const pois = groups ? groups.flatMap((g) => g.items) : dataProp ?? poisContext?.pois ?? [];
  const loading = loadingProp ?? poisContext?.loading ?? false;
  const loadingMore = loadingMoreProp ?? poisContext?.loadingMore ?? false;
  const hasMore = hasMoreProp ?? poisContext?.hasMore ?? false;
  const onLoadMore = onLoadMoreProp ?? poisContext?.loadMore;
  const isRemoving = isRemovingProp ?? poisContext?.isRemoving ?? (() => false);

  const { openCard, closeAllCards, shouldForceClose, handleForceCloseComplete } = useSwipeableList();
  const { hasAnimated } = useEntranceAnimation({ itemCount: pois.length });

  // Comments row action — opens the reusable drawer for any viewer, without the edit drawer. Mounted once
  // (below) via `comments.drawer`; each row calls `comments.open(...)` from its Comments action.
  const comments = useEntityCommentsDrawer();

  const categoryMeta = useCallback(
    (poi: PointOfInterest): CategoryMeta | null => {
      if (!poi.categoryId) {
        return null;
      }

      const category = categoriesContext?.getById(poi.categoryId);

      if (!category) {
        return null;
      }

      return {
        name: category.name || category.code || null,
        icon: category.icon ?? null,
        color: category.color ?? null,
      };
    },
    [categoriesContext]
  );

  const handleEdit = useCallback(
    (poi: PointOfInterest) => {
      closeAllCards();
      onEditPoi?.(poi);
    },
    [onEditPoi, closeAllCards]
  );

  const handleDelete = useCallback(
    (poi: PointOfInterest) => {
      closeAllCards();
      onDeletePoi?.(poi);
    },
    [onDeletePoi, closeAllCards]
  );

  const handleSwipeStateChange = useCallback(
    (id: string, isOpen: boolean) => {
      if (isOpen) {
        openCard(id);
      }
    },
    [openCard]
  );

  // ⚠ A SKELETON IS FOR THE FIRST READ ONLY. `loading && x.length === 0` re-shows it on every
  // refetch of an EMPTY list, so an empty page reads skeleton → "none" → skeleton → "none". Once
  // the first read is back, a refetch keeps the rows OR the empty state and ContentLoadingOverlay
  // says it is reloading. ⚠ The fallback answers "is this all the data there will ever be?" for a
  // caller with no provider. ▶ npm run check-list-states
  const isLoaded = poisContext?.isLoaded ?? (groups !== undefined || dataProp !== undefined);
  const showSkeleton = !isLoaded;

  if (!showSkeleton && pois.length === 0) {
    // A narrowed list that came back empty is a "no match", not a truly-empty event —
    // don't offer "Add" (the fix is to clear the search/filters, not to add one).
    if (hasQueryNarrowing) {
      return (
        <Paper withBorder p="xl" radius="md">
          <EmptyState
            title={t("events:pois.noMatch.title", { defaultValue: "No points of interest match" })}
            description={t("events:pois.noMatch.description", {
              defaultValue: "Try a different search or clear the filters.",
            })}
            iconName="Search"
            size="md"
          />
        </Paper>
      );
    }

    return (
      <Paper withBorder p="xl" radius="md">
        <EmptyState
          title={t("events:pois.emptyState.title", { defaultValue: "No points of interest yet" })}
          description={t("events:pois.emptyState.description", {
            defaultValue: "Add places, vendors and things — hotels, restaurants, museums — for this event.",
          })}
          iconName="MapPin"
          actionLabel={t("events:pois.emptyState.actionLabel", { defaultValue: "Add point of interest" })}
          onAction={onAddPoi}
          size="md"
        />
      </Paper>
    );
  }

  const rowIsSelectable = (poi: PointOfInterest): boolean =>
    isSelectable &&
    ((!!onEditPoi && recordHasRight(poi, "edit")) || (!!onDeletePoi && recordHasRight(poi, "remove")));

  const rowProps = (poi: PointOfInterest): RowProps => ({
    poi,
    name: poi.name || "—",
    meta: categoryMeta(poi),
    price: formatPrice(poi),
    editable: isEditableRow(poi),
    disabled: isDisabled(poi),
    onEdit: onEditPoi ? handleEdit : undefined,
    onDelete: onDeletePoi ? handleDelete : undefined,
    onManageGuests,
    onOpenComments: (p) =>
      comments.open({ eventId: p.eventId ?? "", entityKind: "poi", entityId: p.id ?? "", title: p.name ?? undefined }),
    highlighted: poi.id === highlightedId,
    isDeleting: isRemoving(poi.id),
    selectionColumn: isSelectable,
    selectable: rowIsSelectable(poi),
    selected: selection?.isSelected(poi.id) ?? false,
    selectionMode: selection?.selectionMode ?? false,
    onToggleSelect: selection?.toggle,
  });

  const selectableVisibleIds = pois
    .filter((p) => rowIsSelectable(p))
    .map((p) => p.id)
    .filter((id): id is string => !!id);

  const loadMore = (
    <LoadMoreButton hasMore={hasMore} loading={loading} loadingMore={loadingMore} onLoadMore={onLoadMore} />
  );

  // ── Desktop table ──
  if (isDesktop) {
    return (
      <Stack gap="md">
        <Paper withBorder radius="md" style={{ overflow: "hidden" }}>
          <Table verticalSpacing="sm" horizontalSpacing="md">
            <Table.Thead>
              <Table.Tr>
                {isSelectable && (
                  <Table.Th style={{ width: 44 }}>
                    <Checkbox
                      checked={selection!.allSelected(selectableVisibleIds)}
                      indeterminate={selection!.someSelected(selectableVisibleIds)}
                      onChange={() => selection!.toggleAll(selectableVisibleIds)}
                      disabled={selectableVisibleIds.length === 0}
                      aria-label={t("common:selection.selectAll", { defaultValue: "Select all" })}
                    />
                  </Table.Th>
                )}
                <Table.Th>{t("events:pois.list.columns.name", { defaultValue: "Point of interest" })}</Table.Th>
                <Table.Th style={{ width: 200 }}>
                  {t("events:pois.list.columns.category", { defaultValue: "Category" })}
                </Table.Th>
                <Table.Th style={{ width: 240 }}>
                  {t("events:pois.list.columns.address", { defaultValue: "Address" })}
                </Table.Th>
                <Table.Th style={{ width: 180 }}>
                  {t("events:pois.list.columns.price", { defaultValue: "Price" })}
                </Table.Th>
                <Table.Th style={{ width: 100 }} />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {showSkeleton ? (
                Array.from({ length: 8 }).map((_, i) => (
                  <PoiTableRowSkeleton key={i} selectionColumn={isSelectable} />
                ))
              ) : groups ? (
                groups.map((g) => (
                  <React.Fragment key={g.key}>
                    <Table.Tr>
                      <Table.Td
                        colSpan={(isSelectable ? 1 : 0) + 5}
                        style={{ background: "var(--mantine-color-default-hover)" }}
                      >
                        <Group gap="xs" wrap="nowrap">
                          {g.icon ? <AppIcon iconName={g.icon} size={16} color={g.color ?? undefined} /> : null}
                          <Text fw={700} size="sm" tt="uppercase" c="dimmed">
                            {g.label}
                          </Text>
                        </Group>
                      </Table.Td>
                    </Table.Tr>
                    {g.items.map((poi) => (
                      <PoiTableRow key={poi.id} {...rowProps(poi)} />
                    ))}
                  </React.Fragment>
                ))
              ) : (
                pois.map((poi) => <PoiTableRow key={poi.id} {...rowProps(poi)} />)
              )}
            </Table.Tbody>
          </Table>
        </Paper>
        {!showSkeleton && loadMore}
        {comments.drawer}
      </Stack>
    );
  }

  // ── Tablet / mobile cards ──
  if (showSkeleton) {
    return (
      <Stack gap="sm">
        {Array.from({ length: 6 }).map((_, i) => (
          <PoiCardSkeleton key={i} actions={cardActions} withCheckbox={isSelectable} />
        ))}
      </Stack>
    );
  }

  const renderCard = (poi: PointOfInterest) => (
    <PoiCard
      {...rowProps(poi)}
      forceClose={shouldForceClose(poi.id || "")}
      onForceCloseComplete={() => handleForceCloseComplete(poi.id || "")}
      onSwipeStateChange={(isOpen) => handleSwipeStateChange(poi.id || "", isOpen)}
    />
  );

  // Grouped mobile: a heading per category, then its cards. Entrance animation is
  // skipped here (the section headings make the ordering clear without it).
  if (groups) {
    return (
      <Stack gap="lg">
        {groups.map((g) => (
          <Stack key={g.key} gap="sm">
            <Group gap="xs" wrap="nowrap" px={4}>
              {g.icon ? <AppIcon iconName={g.icon} size={16} color={g.color ?? undefined} /> : null}
              <Text fw={700} size="sm" tt="uppercase" c="dimmed">
                {g.label}
              </Text>
            </Group>
            {g.items.map((poi) => (
              <div key={poi.id}>{renderCard(poi)}</div>
            ))}
          </Stack>
        ))}
        {loadMore}
        {comments.drawer}
      </Stack>
    );
  }

  return (
    <Stack gap="md">
      <motion.div
        variants={hasAnimated ? undefined : entranceListVariants}
        initial={hasAnimated ? false : "hidden"}
        animate={hasAnimated ? undefined : "visible"}
        style={{ display: "flex", flexDirection: "column", gap: "var(--mantine-spacing-sm)" }}
      >
        <AnimatePresence mode="sync">
          {pois.map((poi) => (
            <motion.div
              key={poi.id}
              variants={hasAnimated ? undefined : entranceCardVariants}
              initial={hasAnimated ? false : "hidden"}
              animate={hasAnimated ? undefined : "visible"}
              exit="exit"
              layout
            >
              {renderCard(poi)}
            </motion.div>
          ))}
        </AnimatePresence>
      </motion.div>
      {loadMore}
      {comments.drawer}
    </Stack>
  );
};

export const PoisList = React.memo(PoisListComponent);
